import { LocationDetails, LocationSuggestion } from '@griply/types';
import { debounce, formatLocationDetailsLabel } from '@griply/utils';
import { useCallback, useMemo, useRef, useState } from 'react';

export const MIN_LOCATION_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

// Structural mirrors of the @griply/services payloads. `@griply/hooks` may only depend on
// types + utils, so the shapes are declared here rather than imported from services.
export type LocationSuggestionsRequest = {
  input: string;
  sessionToken: string;
  languageCode?: string;
};

export type LocationDetailsRequest = {
  placeId: string;
  sessionToken: string;
  languageCode: string;
};

export type UseLocationSearchOptions = {
  suggestions: LocationSuggestion[];
  isFetching: boolean;
  fetchSuggestions: (request: LocationSuggestionsRequest) => void;
  getLocationDetails: (
    request: LocationDetailsRequest,
  ) => Promise<LocationDetails>;
  languageCode: string;
  // Each app supplies its own id source — keeping `uuid` out of this package avoids
  // dragging an ESM-only dependency through the barrel into mobile's jest runtime.
  createSessionToken: () => string;
};

export type LocationSearch = {
  suggestions: LocationSuggestion[];
  isSearching: boolean;
  onInputChange: (text: string) => void;
  // closes the search session; null = details unavailable, keep the optimistic label
  resolveLocation: (suggestion: LocationSuggestion) => Promise<string | null>;
};

export const useLocationSearch = ({
  suggestions,
  isFetching,
  fetchSuggestions,
  getLocationDetails,
  languageCode,
  createSessionToken,
}: UseLocationSearchOptions): LocationSearch => {
  // one Google billing session: lazily created, reused per keystroke, closed by the details call
  const sessionTokenRef = useRef<string | null>(null);
  const [isActive, setIsActive] = useState(false);

  const fetchForInput = useCallback(
    (input: string, sessionToken: string, language: string) => {
      fetchSuggestions({ input, sessionToken, languageCode: language });
    },
    [fetchSuggestions],
  );

  const debouncedFetch = useMemo(
    () => debounce(fetchForInput, SEARCH_DEBOUNCE_MS),
    [fetchForInput],
  );

  const onInputChange = useCallback(
    (text: string) => {
      const input = text.trim();

      if (input.length < MIN_LOCATION_SEARCH_LENGTH) {
        setIsActive(false);
        return;
      }

      sessionTokenRef.current = sessionTokenRef.current ?? createSessionToken();
      setIsActive(true);
      debouncedFetch(input, sessionTokenRef.current, languageCode);
    },
    [createSessionToken, debouncedFetch, languageCode],
  );

  const resolveLocation = useCallback(
    async (suggestion: LocationSuggestion) => {
      const sessionToken = sessionTokenRef.current;
      sessionTokenRef.current = null;
      setIsActive(false);

      if (!sessionToken) {
        return null;
      }

      try {
        const details = await getLocationDetails({
          placeId: suggestion.placeId,
          sessionToken,
          languageCode,
        });

        return formatLocationDetailsLabel(details);
      } catch {
        // already Sentry-reported by the app wrapper; the caller keeps the optimistic label
        return null;
      }
    },
    [getLocationDetails, languageCode],
  );

  return useMemo(
    () => ({
      suggestions: isActive ? suggestions : [],
      isSearching: isActive && isFetching,
      onInputChange,
      resolveLocation,
    }),
    [isActive, suggestions, isFetching, onInputChange, resolveLocation],
  );
};
